export default {
  slug: "/hollywood-smile",
  group: { href: "/dental", label: { en: "Dental care", ar: "طب الأسنان" } },
  heroImage: "/assets/images/ai/services/dental/smile.webp",

  en: {
    title: "Hollywood Smile in Istanbul",
    eyebrow: "Dental care",
    lead:
      "A Hollywood Smile is a plan rather than a single procedure: the shape, colour and proportions of the " +
      "front teeth are designed together, then built with veneers, crowns and — where the gum line is uneven — " +
      "gum contouring. Most plans are finished in five to seven days.",
    cta: "Book a free assessment",
    heroAlt: "Hollywood Smile consultation at Platinya Clinic",
    stats: [
      { value: "5–7 days", label: "Typical plan" },
      { value: "8–20", label: "Teeth usually involved" },
      { value: "10–15 years", label: "Lifespan with care" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "The design has to fit the face and the bite. A smile that looks right in a photograph can be wrong " +
        "for a mouth that grinds, or for lips that show a lot of gum.",
      items: [
        "Front teeth that are chipped, worn at the edges, or discoloured in a way whitening will not change",
        "Small gaps, or teeth that are rotated or slightly out of line but not enough to need orthodontics",
        "Old crowns or large front fillings that no longer match the teeth beside them",
        "An uneven gum line, where some teeth look longer than others",
      ],
      note:
        "Teeth and gums have to be healthy first. Active decay, gum disease or an unstable bite is treated " +
        "before any veneer is made; otherwise the new work is built on something that is still moving.",
    },
    techniques: {
      heading: "How the smile is built",
      intro:
        "The mix is decided by how much tooth is left, how much enamel can be spared, and how much change " +
        "you actually want.",
      items: [
        {
          name: "Digital design and trial smile",
          text:
            "Photographs and an intraoral scan are used to design the smile on screen, with proportions taken " +
            "from your face rather than from a template. The design is printed as a mock-up and bonded " +
            "temporarily over your own teeth, so you can see it, speak with it and change it before anything " +
            "permanent is done.",
        },
        {
          name: "Veneers and crowns",
          text:
            "Veneers are thin ceramic facings bonded to the front of intact teeth and need little or no " +
            "preparation. Crowns cover the whole tooth and are used where it is heavily filled, root-treated " +
            "or broken down. Most makeovers mix the two: veneers through the middle, crowns where a tooth " +
            "needs support.",
        },
        {
          name: "Gum contouring",
          text:
            "An uneven gum line is reshaped with a laser or a fine blade. Where the teeth themselves are short " +
            "because gum and bone sit low, crown lengthening is needed — a small surgical step that adds " +
            "weeks to the plan, not days.",
        },
      ],
    },
    journey: {
      heading: "From consultation to the final smile",
      steps: [
        {
          name: "Assessment and design",
          text:
            "Examination, x-rays, photographs and a shade record. The bite is checked for wear and grinding. " +
            "The design is shown to you with its limits: which teeth, how much enamel, and how white is " +
            "believable against your skin.",
        },
        {
          name: "Trial smile",
          text:
            "A mock-up is bonded over the teeth for a day or two. This is the moment to be critical — length, " +
            "symmetry, how it looks when you speak — because it is still reversible.",
        },
        {
          name: "Preparation",
          text:
            "Under local anaesthetic the enamel is reduced by roughly the thickness the ceramic will add, " +
            "impressions or a scan are taken, and temporary restorations are fitted the same day. Teeth are " +
            "usually sensitive for a few days.",
        },
        {
          name: "Try-in",
          text:
            "The ceramic returns from the laboratory and is checked dry for fit, shade and bite before " +
            "cementing. Small adjustments at this stage are normal; a remake adds days.",
        },
        {
          name: "Fitting and review",
          text:
            "The restorations are bonded, the bite is refined, and a night guard is made if you grind. Review " +
            "at one to two weeks, then routine cleaning — the margins are where these restorations are kept " +
            "alive.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro:
        "This is not one operation but a series of permanent decisions. These are the ones worth knowing " +
        "before the first tooth is prepared.",
      items: [
        "Enamel removed for veneers does not grow back; those teeth will always need a restoration",
        "Sensitivity to hot and cold for days or weeks, occasionally longer",
        "Chipping or debonding, most often in people who grind or bite their nails",
        "A shade that reads too opaque or too uniform, especially when many teeth are treated at once",
        "Gum inflammation or recession at the margins where cleaning becomes difficult",
      ],
      note:
        "The usual disappointment is not the ceramic but the plan — too many teeth, too white a shade, or a " +
        "bite left unbalanced so the front teeth carry load they were never designed for.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "How long does it take?",
          a:
            "Five to seven days at the clinic for a straightforward case: two visits for preparation and " +
            "fitting, with the laboratory working in between. If gum surgery, a bite reconstruction or " +
            "extractions are needed, the plan is staged over weeks or months.",
        },
        {
          q: "Will it look natural?",
          a:
            "It can, but natural is a decision rather than a default. Real teeth are not one flat colour: the " +
            "neck is warmer, the edge more translucent, the surface has texture. A good ceramist reproduces " +
            "that; a very white, very opaque set reads as false whatever ceramic is used.",
        },
        {
          q: "Do all my teeth need treating?",
          a:
            "Usually eight to ten upper teeth, which is what shows when you smile and speak. Lower teeth are " +
            "often left alone unless they show. Treating more than is visible adds cost and enamel loss " +
            "without changing the smile.",
        },
        {
          q: "How long do they last?",
          a:
            "Well-made veneers commonly last ten to fifteen years; crowns similar or longer. Lifespan is " +
            "decided less by the ceramic than by the bite, grinding, and how carefully the margins are cleaned.",
        },
      ],
    },
    ctaBand: {
      heading: "Send photographs of your smile",
      text:
        "A front view, a close view of the teeth and one with the lips at rest are enough for a first opinion " +
        "on what is possible, roughly how many teeth it involves, and what it would take.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability can only be judged after examining the teeth, " +
      "the gums, the bite and the medical history; results vary between patients.",
  },

  ar: {
    title: "الابتسامة الهوليوودية في إسطنبول",
    eyebrow: "طب الأسنان",
    lead:
      "الابتسامة الهوليوودية خطة علاج لا إجراء واحد: تُصمَّم أشكال الأسنان الأمامية وألوانها ونسبها معاً، " +
      "ثم تُنفَّذ بالقشور والتركيبات، ومعها — حيث يكون خط اللثة غير متساوٍ — إعادة تشكيل اللثة. " +
      "وتنتهي معظم الخطط في خمسة إلى سبعة أيام.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "استشارة الابتسامة الهوليوودية في عيادة بلاتينيا",
    stats: [
      { value: "٥–٧ أيام", label: "مدة الخطة عادةً" },
      { value: "٨–٢٠ سناً", label: "عدد الأسنان غالباً" },
      { value: "١٠–١٥ سنة", label: "العمر مع العناية" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "يجب أن يناسب التصميم الوجه والإطباق. فالابتسامة التي تبدو صحيحة في صورة قد تكون خاطئة لفم " +
        "يعاني صرير الأسنان، أو لشفاه تُظهر كثيراً من اللثة.",
      items: [
        "أسنان أمامية مكسورة أو متآكلة الحواف أو متغيّرة اللون لا يغيّرها التبييض",
        "فراغات صغيرة، أو أسنان مائلة أو غير منتظمة بما لا يستدعي تقويماً",
        "تركيبات قديمة أو حشوات كبيرة في المقدمة لم تعد متناسقة مع الأسنان المجاورة",
        "خط لثة غير متساوٍ، فتبدو بعض الأسنان أطول من غيرها",
      ],
      note:
        "يجب أن تكون الأسنان واللثة سليمة أولاً. يُعالَج التسوّس النشط أو أمراض اللثة أو الإطباق غير " +
        "المستقر قبل صناعة أي قشرة، وإلا بُني العمل الجديد على أساس ما زال يتحرّك.",
    },
    techniques: {
      heading: "كيف تُبنى الابتسامة",
      intro:
        "يُحدَّد المزيج بحسب ما تبقّى من السن، ومقدار المينا التي يمكن الحفاظ عليها، وحجم التغيير الذي " +
        "تريده فعلاً.",
      items: [
        {
          name: "التصميم الرقمي والابتسامة التجريبية",
          text:
            "تُستخدم الصور والمسح داخل الفم لتصميم الابتسامة على الشاشة، وتُؤخذ النسب من الوجه لا من قالب " +
            "جاهز. ثم يُطبع التصميم كتجربة تُلصق مؤقتاً فوق أسنانك، لتراها وتتكلّم بها وتغيّرها قبل أي " +
            "عمل دائم.",
        },
        {
          name: "القشور والتركيبات",
          text:
            "القشور واجهات خزفية رقيقة تُلصق على الوجه الأمامي للأسنان السليمة، وتحتاج تحضيراً بسيطاً أو لا " +
            "تحتاج. أما التركيبات فتغطي السن كاملاً وتُستخدم حين يكون السن كثير الحشوات أو معالَج العصب أو " +
            "متهدّماً. ومعظم الحالات تخلط بينهما: قشور في الوسط وتركيبات حيث يحتاج السن إلى دعم.",
        },
        {
          name: "إعادة تشكيل اللثة",
          text:
            "إذا كان خط اللثة غير متساوٍ يُعاد تشكيله بالليزر أو بشفرة دقيقة. وحين تكون الأسنان قصيرة لأن " +
            "اللثة والعظم منخفضان، يلزم تطويل التاج — خطوة جراحية صغيرة تضيف أسابيع إلى الخطة لا أياماً.",
        },
      ],
    },
    journey: {
      heading: "من الاستشارة إلى الابتسامة النهائية",
      steps: [
        {
          name: "التقييم والتصميم",
          text:
            "فحص وأشعة سينية وصور وتسجيل للون، مع فحص الإطباق بحثاً عن التآكل وصرير الأسنان. ويُعرض عليك " +
            "التصميم مع حدوده: أي أسنان، وكم من المينا، وأي درجة بياض تكون مقنعة مع لون بشرتك.",
        },
        {
          name: "الابتسامة التجريبية",
          text:
            "تُلصق تجربة فوق الأسنان ليوم أو يومين. هذه لحظة النقد: الطول والتناسق والشكل عند الكلام، " +
            "فالأمر ما زال قابلاً للتراجع.",
        },
        {
          name: "التحضير",
          text:
            "تحت تخدير موضعي تُبرد المينا بمقدار سماكة الخزف تقريباً، وتُؤخذ الطبعات أو المسح، وتُركَّب " +
            "تلبيسات مؤقتة في اليوم نفسه. وتبقى الأسنان حساسة أياماً قليلة عادةً.",
        },
        {
          name: "التجربة في الفم",
          text:
            "يعود الخزف من المختبر ويُفحص جافاً من حيث الملاءمة واللون والإطباق قبل التثبيت. والتعديلات " +
            "الصغيرة في هذه المرحلة طبيعية، أما الإعادة فتضيف أياماً.",
        },
        {
          name: "التثبيت والمراجعة",
          text:
            "تُلصق التركيبات ويُضبط الإطباق ويُصنع واقٍ ليلي إن كنت تعاني صرير الأسنان. ثم مراجعة بعد أسبوع " +
            "أو أسبوعين، وتنظيف دوري بعدها — فالحواف هي ما يحفظ عمر هذه التركيبات.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro:
        "هذه ليست عملية واحدة بل سلسلة قرارات دائمة. وهذه أهمّها قبل برد السن الأول.",
      items: [
        "المينا المُزالة للقشور لا تعود؛ وستحتاج تلك الأسنان إلى ترميم دائم",
        "حساسية للحار والبارد لأيام أو أسابيع، وأحياناً أطول",
        "كسر القشرة أو انفصالها، وأكثر ما يحدث عند من يعاني صرير الأسنان أو يقضم أظافره",
        "لون يبدو معتماً أو موحّداً أكثر من اللازم، خصوصاً إذا عولج عدد كبير من الأسنان دفعة واحدة",
        "التهاب أو انحسار في اللثة عند الحواف حيث يصعب التنظيف",
      ],
      note:
        "خيبة الأمل المعتادة ليست في الخزف بل في الخطة — أسنان أكثر من اللازم، أو لون أفتح من اللازم، أو " +
        "إطباق تُرك غير متوازن فتتحمّل الأسنان الأمامية حِملاً لم تُصمَّم له.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "كم تستغرق؟",
          a:
            "خمسة إلى سبعة أيام في العيادة للحالة المباشرة: زيارتان للتحضير والتثبيت، والمختبر يعمل " +
            "بينهما. وإذا لزمت جراحة لثة أو إعادة بناء إطباق أو قلع أسنان، تُقسَّم الخطة على أسابيع أو أشهر.",
        },
        {
          q: "هل ستبدو طبيعية؟",
          a:
            "يمكن ذلك، لكن الطبيعية قرار لا نتيجة تلقائية. الأسنان الحقيقية ليست لوناً واحداً مسطّحاً: العنق " +
            "أدفأ، والحدّ أكثر شفافية، والسطح فيه نسيج. والفنّي الماهر يعيد ذلك؛ أما أسنان شديدة البياض " +
            "وشديدة العتامة فتُقرأ مصطنعة أياً كان نوع الخزف.",
        },
        {
          q: "هل تُعالَج كل الأسنان؟",
          a:
            "عادةً ثمانية إلى عشرة أسنان علوية، وهي ما يظهر عند الابتسام والكلام. والأسنان السفلية تُترك " +
            "غالباً إلا إذا كانت ظاهرة. ومعالجة أكثر من الظاهر تضيف كلفة وفقداً في المينا دون أن تغيّر " +
            "الابتسامة.",
        },
        {
          q: "كم تدوم؟",
          a:
            "القشور الجيدة الصنع تدوم عادةً عشر إلى خمس عشرة سنة، والتركيبات مثلها أو أكثر. والعمر يحسمه " +
            "الإطباق وصرير الأسنان والعناية بتنظيف الحواف أكثر مما يحسمه نوع الخزف.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صور ابتسامتك",
      text:
        "صورة أمامية، وصورة قريبة للأسنان، وصورة والشفاه مرتاحة — تكفي لرأي أوّلي فيما يمكن عمله، وعدد " +
        "الأسنان تقريباً، وما يتطلّبه الأمر.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يمكن الحكم على الملاءمة إلا بعد فحص الأسنان واللثة والإطباق " +
      "والتاريخ المرضي؛ والنتائج تختلف من مريض لآخر.",
  },
};
